import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import { NotificationService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { Notification } from "@/utils/types/notification";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const NotificationListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const {
    data,
    isLoading,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: ["notifications", page, pageSize],
    queryFn: () => NotificationService.getAllNotifications({ page, pageSize }),
  });

  const handleEdit = (id: string) => {
    navigate(`${navigationRoutes.Notification_ADD}/${id}`);
  };

  const handleDelete = async (id: string) => {
    await NotificationService.deleteNotification(id);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    refetchNotifications();
  };

  const columns: ProColumns<Notification>[] = [
    {
      title: "Notification Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Channel",
      dataIndex: "channel",
      render: (_, record) => {
        const channelMap: { [key: number]: string } = {
          1: "Email",
          2: "SMS",
          3: "System",
          4: "WhatsApp",
        };
        return channelMap[record.channel] || "Unknown";
      },
    },
    {
      title: "Schema",
      dataIndex: "schema_name",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEdit(String(record.id))}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit Notification"
        />,
        <Popconfirm
          key="delete"
          title={`Delete notification "${record.name}"?`}
          onConfirm={() => handleDelete(String(record.id))}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Notification"
          />
        </Popconfirm>,
      ],
    },
  ];
  const toolBarRender = () => [
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={() => navigate(navigationRoutes.Notification_ADD)}
      title="Add New Notification"
    >
      Add Notification
    </CustomButton>,
  ];

  return (
    <BaseTable<Notification>
      loading={isLoading}
      columns={columns}
      dataSource={data?.results ?? []}
      page={page}
      pageSize={pageSize}
      total={data?.count}
      onPageChange={(page, pageSize) => {
        setPage(page);
        setPageSize(pageSize ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", pageSize.toString());
        navigate(`${navigationRoutes.Notification}?${searchParams.toString()}`);
        refetchNotifications();
      }}
      toolBarRender={toolBarRender}
      rowKey="id"
      headerTitle="Notifications List"
      request={() => refetchNotifications()}
    />
  );
};

export default NotificationListPage;
